/**
 * F-174 — criar alguém (equipe ou login de portal) cria também a conta de
 * login no SSO (Keycloak): ligada, sem senha, com o papel e na organização
 * desta empresa. A primeira senha é a do "Redefinir senha", temporária.
 *
 * Se o SSO falha, ninguém é criado no ERP (a API desfaz tudo e apaga a conta
 * que ela tinha acabado de criar) — as mensagens dizem isso.
 */

/** Códigos da criação do login no SSO → mensagem para o admin. */
export const LOGIN_ACCOUNT_ERRORS: Record<string, string> = {
  keycloak_unavailable: 'O SSO não respondeu. Ninguém foi criado — tente de novo em instantes.',
  organization_check_unavailable:
    'Não foi possível confirmar no SSO a organização desta empresa. Ninguém foi criado — fale com o suporte.',
  organization_join_failed:
    'O SSO não deixou incluir a pessoa na organização desta empresa. Ninguém foi criado — fale com o suporte.',
  realm_role_unavailable:
    'O SSO não deixou dar o papel a esta pessoa. Ninguém foi criado — fale com o suporte.',
  login_account_rejected:
    'O SSO recusou o cadastro — confira o nome (sem símbolos como & ou @). Ninguém foi criado.',
};

/**
 * Spec 062 — trocar o papel na edição troca também o papel da conta no SSO (é de
 * lá que ele vale). Se o SSO recusa ou falha, nada é alterado: nem lá, nem aqui.
 */
export const ROLE_CHANGE_ERRORS: Record<string, string> = {
  role_conflict:
    'O papel desta pessoa não muda por aqui: a conta dela no SSO entra em outra empresa, é do portal ou administra o SSO. Nada foi alterado.',
  realm_role_unchanged:
    'O SSO não aplicou o papel (ele pode vir de um grupo do SSO). Nada foi alterado — fale com o suporte.',
  realm_role_unavailable: 'O SSO não deixou trocar o papel desta pessoa. Nada foi alterado — fale com o suporte.',
  keycloak_unavailable: 'O SSO não respondeu. Nada foi alterado — tente de novo em instantes.',
};

/** O aviso do diálogo de criação no modo SSO. */
export const LOGIN_ACCOUNT_HINT =
  'A conta de login é criada no SSO, sem senha. Depois de criar, gere a senha temporária em "Redefinir senha" e envie à pessoa.';

/**
 * F-180 — o e-mail de alguém que JÁ EXISTE no ERP sem acesso a esta empresa (a
 * equipe que veio do TiFlux, por exemplo) não cria outra pessoa: o cadastro
 * dela é aproveitado. O aviso do diálogo de criação diz isso antes.
 */
export const ADOPTION_HINT =
  'Se o e-mail já for de alguém do ERP sem acesso a esta empresa (por exemplo, quem veio do TiFlux), o cadastro dessa pessoa é aproveitado — com os tickets e o histórico dela.';

/** Os papéis que o "Novo usuário" dá, como a tela os chama. */
const ADOPTED_ROLE_LABELS: Record<string, string> = { admin: 'Admin', attendant: 'Atendente' };

/**
 * O que o "Redefinir senha" diz logo depois de uma adoção (F-180): a pessoa já
 * existia, o cadastro dela ficou (o nome é o dela, não o digitado), e o que
 * nasceu foi o acesso a esta empresa.
 */
export function adoptedNotice(person: { name: string; role: string }): string {
  const role = ADOPTED_ROLE_LABELS[person.role];
  return (
    `Esta pessoa já existia no ERP, como ${person.name}. O cadastro dela foi mantido, com os tickets e o histórico, ` +
    `e ela ganhou acesso a esta empresa${role ? ` como ${role}` : ''}. Falta só a senha: gere a temporária abaixo e envie a ela.`
  );
}
